import { useEffect, useRef, useState } from 'react'
import { useSession } from 'gunsi-auth'
import { BottomSheet } from '../components/BottomSheet.jsx'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { EmojiPicker } from '../components/EmojiPicker.jsx'
import { youOf } from '../util/you.js'
import { youSummaryByCurrency } from '../util/balances.js'
import { fmtMoney } from '../format.js'
import * as api from '../api.js'

export function GroupFormSheet({ open, onClose, group }) {
  const t = useEvenI18n()
  const { session } = useSession()
  const { confirm, notify } = useConfirm()
  const {
    createGroup, updateGroup, removeGroup, expenses, settlements,
    tallySync, tallyAccess, setGroupTallySync, backfillGroupTally, wipeGroupTally,
  } = useEven()
  const gExp = group ? expenses.filter(e => e.groupId === group.id) : []
  const gSet = group ? settlements.filter(s => s.groupId === group.id) : []

  const isEdit = Boolean(group)
  const myUserId = session?.user?.id ?? null
  // Gruppi legacy (LS) senza ownerId: chi li vede ne è l'owner
  const isOwner = !isEdit || !group.ownerId || group.ownerId === myUserId

  const [name, setName]     = useState('')
  const [emoji, setEmoji]   = useState('👥')
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [members, setMembers] = useState([])
  // Typeahead account Gunsi: il campo nome stesso cerca mentre scrivi.
  // Selezioni un account → member linkato; ignori il dropdown → resta ghost.
  const [activeRow, setActiveRow]     = useState(null)  // member.id col focus
  const [linkResults, setLinkResults] = useState([])
  const [linkWarn, setLinkWarn]       = useState(null)  // member.id linkato ma senza accesso a Even

  // Dipendenze sugli ID e non sugli oggetti: `refresh()` su focus rimpiazza
  // i gruppi con istanze nuove e cancellerebbe i member appena digitati
  useEffect(() => {
    if (!open) return
    setActiveRow(null); setLinkResults([]); setLinkWarn(null)
    if (group) {
      setName(group.name ?? '')
      setEmoji(group.emoji || '👥')
      setMembers(group.members ?? [])
    } else {
      setName('')
      setEmoji('👥')
      const youName = session?.user?.name || t.newGroup.you
      setMembers([{ id: '__you__', name: youName, isYou: true, userId: myUserId }])
    }
    // Come negli altri form: il blocco anti-doppio-invio va sciolto a ogni
    // apertura, altrimenti resta alzato dal salvataggio precedente
    savingRef.current = false
    setSaving(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group?.id, myUserId])

  const youRow = isEdit ? youOf({ members }, myUserId) : members.find(m => m.isYou)
  const isYouRow = (m) => Boolean(youRow && m.id === youRow.id)

  // Search debounced sul nome della riga attiva (solo righe non linkate)
  useEffect(() => {
    if (activeRow === null) return
    const row = members.find(m => m.id === activeRow)
    if (!row || row.userId) { setLinkResults([]); return }
    const q = (row.name ?? '').trim()
    if (q.length < 2) { setLinkResults([]); return }
    let stale = false
    const timer = setTimeout(async () => {
      try {
        const res = await api.searchUsers(q, 'even')
        if (stale) return
        const taken = new Set(members.map(mm => mm.userId).filter(Boolean))
        setLinkResults((res?.users ?? []).filter(u => !taken.has(u.id)))
      } catch {
        if (!stale) setLinkResults([])
      }
    }, 250)
    return () => { stale = true; clearTimeout(timer) }
  }, [members, activeRow])

  function addMember() {
    setMembers(m => [...m, { id: Math.random().toString(36).slice(2), name: '', userId: null }])
  }
  function setMemberName(idx, value) {
    setMembers(m => m.map((mm, i) => i === idx ? { ...mm, name: value } : mm))
  }
  function removeMember(idx) {
    setMembers(m => m.filter((_, i) => i !== idx))
  }
  function linkTo(idx, memberId, user) {
    setMembers(m => m.map((mm, i) => i === idx
      ? { ...mm, userId: user.id, name: user.name || mm.name }
      : mm))
    setLinkResults([])
    setActiveRow(null)
    setLinkWarn(user.hasApp === false ? memberId : null)
  }
  function unlinkMember(idx) {
    setMembers(m => m.map((mm, i) => i === idx ? { ...mm, userId: null } : mm))
    setLinkWarn(null)
  }

  // Come negli altri form: due tap veloci creavano due gruppi
  const savingRef = useRef(false)
  const [saving, setSaving] = useState(false)

  async function onSubmit(e) {
    e.preventDefault()
    if (savingRef.current) return
    const clean = members
      .filter(m => isYouRow(m) || (m.name ?? '').trim())
      .map(m => ({ ...m, name: (m.name ?? '').trim() }))
    if (!name.trim() || clean.length < 1) return
    const patch = isOwner
      ? { name: name.trim(), emoji, members: clean }
      : { name: name.trim(), emoji }
    savingRef.current = true
    setSaving(true)
    const release = () => { savingRef.current = false; setSaving(false) }
    if (isEdit) {
      try {
        await updateGroup(group.id, patch)
      } catch (err) {
        release()
        if (err?.code === 'member_has_entries') {
          const names = (err.detail?.members ?? []).map(m => m.name).filter(Boolean).join(', ')
          await notify({ message: t.newGroup.memberHasEntries.replace('{names}', names || '—') })
          setMembers(group.members ?? [])   // ripristina la lista membri
          return
        }
        throw err
      }
    } else {
      try {
        await createGroup({ name: name.trim(), emoji, members: clean })
      } catch (err) { release(); throw err }
    }
    onClose()
  }

  // Archivia / riapri. Non cancella niente: toglie il gruppo dai totali e
  // dalla pagina Persone, perché a viaggio finito restano quasi sempre saldi
  // che nessuno riscuoterà. Se ce ne sono, lo diciamo prima.
  async function onToggleArchive() {
    if (!group) return
    const archived = Boolean(group.archivedAt)
    if (!archived) {
      const open = youSummaryByCurrency(group, gExp, gSet, youOf(group, myUserId)?.id)
        .filter(s => Math.abs(s.net) >= 0.005)
      const msg = open.length
        ? t.group.archiveConfirmOpen.replace('{amounts}',
            open.map(s => fmtMoney(Math.abs(s.net), s.currency)).join(' · '))
        : t.group.archiveConfirm
      if (!(await confirm({ message: msg, okLabel: t.group.archive }))) return
    }
    await updateGroup(group.id, { archivedAt: archived ? null : new Date().toISOString() })
    onClose()
  }

  async function onDelete() {
    if (!group) return
    const ok = await confirm({
      message: t.group.deleteConfirm.replace('{name}', group.name),
      okLabel: t.common.delete,
      danger: true,
    })
    if (!ok) return
    await removeGroup(group.id)
    onClose()
  }

  // Toggle personale "quota su Tally": visibile solo se l'utente ha accesso
  // a Tally ed è un member linkato del gruppo
  const amLinkedMember = isEdit && (group.members ?? []).some(m => m.userId === myUserId)
  const tallyOn = isEdit ? Boolean(tallySync?.[group.id]) : false

  async function onToggleTally(e) {
    const enabled = e.target.checked
    const res = await setGroupTallySync(group.id, enabled)
    if (enabled && res?.pending > 0) {
      const ok = await confirm({
        message: t.group.tallyBackfillConfirm.replace('{n}', String(res.pending)),
        okLabel: t.group.tallyBackfillOk,
      })
      if (ok) await backfillGroupTally(group.id)
    }
    if (!enabled && res?.synced > 0) {
      const ok = await confirm({
        message: t.group.tallyWipeConfirm.replace('{n}', String(res.synced)),
        okLabel: t.group.tallyWipeOk,
        cancelLabel: t.group.tallyWipeCancel,
        danger: true,
      })
      if (ok) await wipeGroupTally(group.id)
    }
  }

  // Reconcile manuale: ripara qualsiasi drift (sync fallite, reset su Tally,
  // import su Even) — il backfill è idempotente e pulisce anche le zombie
  const [resyncBusy, setResyncBusy] = useState(false)
  async function onResyncTally() {
    if (resyncBusy) return
    // Conferma obbligatoria: il pulsante vive in mezzo ai campi del form ed
    // era a un tap accidentale di distanza dal riscrivere il Tally
    const ok = await confirm({
      message: t.group.tallyResyncConfirm,
      okLabel: t.group.tallyResync,
    })
    if (!ok) return
    setResyncBusy(true)
    try {
      const res = await backfillGroupTally(group.id)
      if (res) await notify({ message: t.group.tallyResyncDone.replace('{n}', String(res.synced ?? 0)) })
    } finally {
      setResyncBusy(false)
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} className="bsheet-form">
      <form onSubmit={onSubmit} className="form-sheet">
        <header className="form-sheet-head">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t.common.cancel}</button>
          <h2>{isEdit ? t.group.edit : t.newGroup.title}</h2>
          <button type="submit" className="btn btn-primary" disabled={saving}>{isEdit ? t.common.save : t.newGroup.create}</button>
        </header>

        <div className="form-sheet-body">
        <div className="form-row">
          <label>{t.newGroup.name}</label>
          <div className="desc-row">
            <button
              type="button"
              className="emoji-btn"
              onClick={() => setEmojiOpen(true)}
              aria-label={t.newGroup.emoji}
            >{emoji}</button>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t.newGroup.namePh}
              autoFocus
            />
          </div>
        </div>

        <div className="form-row">
          <label>{t.newGroup.members}</label>
          {!isOwner ? (
            <>
              <div className="member-list">
                {members.map(m => (
                  <div key={m.id} className="member-row">
                    <input type="text" value={isYouRow(m) ? (m.name || t.newGroup.you) : m.name} readOnly className={isYouRow(m) ? 'is-you' : ''} />
                  </div>
                ))}
              </div>
              <div className="form-hint">{t.newGroup.membersLocked}</div>
            </>
          ) : (
            <div className="member-list">
              {members.map((m, idx) => (
                <div key={m.id} className="member-block">
                  <div className="member-row">
                    <input
                      type="text"
                      value={m.name}
                      onChange={e => setMemberName(idx, e.target.value)}
                      onFocus={() => { if (!isYouRow(m) && !m.userId) setActiveRow(m.id) }}
                      onBlur={() => setTimeout(() => setActiveRow(cur => cur === m.id ? null : cur), 150)}
                      onKeyDown={e => { if (e.key === 'Escape') setActiveRow(null) }}
                      placeholder={t.newGroup.memberPh}
                      readOnly={isYouRow(m)}
                      className={isYouRow(m) ? 'is-you' : m.userId ? 'is-linked' : ''}
                    />
                    {!isYouRow(m) && m.userId && (
                      <button
                        type="button"
                        className="member-linked-chip"
                        onClick={() => unlinkMember(idx)}
                        title={t.newGroup.unlink}
                        aria-label={t.newGroup.unlink}
                      >✓ {t.newGroup.linked}</button>
                    )}
                    {!isYouRow(m) && (
                      <button type="button" className="btn btn-icon" onClick={() => removeMember(idx)} aria-label="remove">×</button>
                    )}
                  </div>
                  {activeRow === m.id && !m.userId && linkResults.length > 0 && (
                    <ul className="link-suggest">
                      {linkResults.map(u => (
                        <li key={u.id}>
                          {/* onMouseDown: scatta prima del blur dell'input */}
                          <button type="button" className="link-suggest-item" onMouseDown={e => { e.preventDefault(); linkTo(idx, m.id, u) }}>
                            {u.image
                              ? <img className="link-suggest-avatar" src={u.image} alt="" />
                              : <span className="link-suggest-avatar">{(u.name || '?')[0].toUpperCase()}</span>}
                            <span className="link-suggest-name">{u.name}</span>
                            {u.hasApp === false && <span className="link-suggest-warn">{t.newGroup.noAccessShort}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {linkWarn === m.id && <div className="form-hint warn">{t.newGroup.linkNoAccess}</div>}
                </div>
              ))}
              <button type="button" className="btn btn-ghost btn-small" onClick={addMember}>+ {t.newGroup.addMember}</button>
            </div>
          )}
        </div>

        {isEdit && tallyAccess && amLinkedMember && (
          <div className="form-row">
            <label>{t.group.tallyTitle}</label>
            <label className="toggle-row">
              <input type="checkbox" checked={tallyOn} onChange={onToggleTally} />
              <span>{t.group.tallyToggle}</span>
            </label>
            <div className="form-hint">{t.group.tallyHint}</div>
            {tallyOn && (
              <button
                type="button"
                className="btn btn-ghost btn-small tally-resync-btn"
                disabled={resyncBusy}
                onClick={onResyncTally}
              >⟳ {t.group.tallyResync}</button>
            )}
          </div>
        )}

        {isEdit && isOwner && (
          <div className="form-row archive-row">
            <button type="button" className="btn btn-secondary" onClick={onToggleArchive}>
              {group.archivedAt ? t.group.unarchive : t.group.archive}
            </button>
            <div className="form-hint">
              {group.archivedAt ? t.group.unarchiveHint : t.group.archiveHint}
            </div>
          </div>
        )}

        {isEdit && isOwner && (
          <div className="form-row">
            <button type="button" className="btn btn-danger" onClick={onDelete}>{t.group.delete}</button>
          </div>
        )}

        <BottomSheet open={emojiOpen} onClose={() => setEmojiOpen(false)} className="bsheet-select">
          <div className="select-sheet">
            <header className="select-sheet-head">{t.newGroup.emoji}</header>
            <EmojiPicker
              value={emoji}
              onChange={(em) => { setEmoji(em); setEmojiOpen(false) }}
              searchPlaceholder={t.common.searchEmojiPh}
            />
          </div>
        </BottomSheet>
        </div>
      </form>
    </BottomSheet>
  )
}
